import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { apiGet, apiSend } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { CaretLeft, CaretRight, CalendarDots, Plus } from "@phosphor-icons/react";
import {
  CalendarGrid,
  CalendarSummary,
  DaySidebar,
  CancelSessionDialog,
  SessionMembersDialog,
  STATUS_STYLES,
  STATUS_KEYS,
  startOfWeek,
  addDays,
  fmtDate,
} from "@/components/calendar";
import { RescheduleDialog } from "@/components/therapist/therapies/RescheduleDialog";
import { AddSessionDialog, type ManualBookingBody } from "@/components/therapist/calendar/AddSessionDialog";
import type { CalendarSession, ViewMode } from "@/components/calendar";
import type { ScheduleOverviewRules } from "@/lib/schedule-overview";
import { utcToWallClock, todayInZone } from "@/lib/timezone";
import { DEFAULT_BOOKING_HORIZON_DAYS } from "@/lib/booking-horizon";

export const Route = createFileRoute("/dashboard/therapist/calendar")({
  component: TherapistCalendar,
});

const DEFAULT_SCHEDULE_OVERVIEW: ScheduleOverviewRules = {
  availableDays: [],
  blockedDates: [],
  addedDates: [],
  bookingHorizonDays: DEFAULT_BOOKING_HORIZON_DAYS,
  minBookingLeadDays: 2,
};

function TherapistCalendar() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dayNames = t("calendar.days", { returnObjects: true }) as string[];
  const monthNames = t("calendar.months", { returnObjects: true }) as string[];

  // "Today" is the Sarajevo calendar date, held as a local-midnight Date used
  // only as a (year, month, day) container. This keeps the grid cells and the
  // session buckets (also keyed by Sarajevo date) consistent for a therapist
  // whose browser is in another timezone.
  const today = useMemo(() => {
    const [y, m, d] = todayInZone().split("-").map(Number) as [number, number, number];
    return new Date(y, m - 1, d);
  }, []);

  const [view, setView] = useState<ViewMode>("week");
  const [anchor, setAnchor] = useState(today);
  const [selectedDate, setSelectedDate] = useState<Date | null>(today);
  const [reschedulingSession, setReschedulingSession] = useState<CalendarSession | null>(null);
  const [cancelingSession, setCancelingSession] = useState<CalendarSession | null>(null);
  const [membersSession, setMembersSession] = useState<CalendarSession | null>(null);
  const [showAddSession, setShowAddSession] = useState(false);

  const { data: meData } = useQuery({
    queryKey: ["therapist", "profile", "me"],
    queryFn: () => apiGet<{ id: string } | null>("/api/therapist/me", "").catch(() => null),
  });

  const { data: scheduleOverview } = useQuery<ScheduleOverviewRules>({
    queryKey: ["schedule-overview", meData?.id],
    queryFn: async () => {
      const res = await fetch(`/api/therapists/${meData?.id}/schedule-overview`);
      if (!res.ok) {
        return DEFAULT_SCHEDULE_OVERVIEW;
      }
      return res.json();
    },
    enabled: !!meData?.id,
  });

  const { from, to, days } = useMemo(() => {
    if (view === "week") {
      const monday = startOfWeek(anchor);
      const sunday = addDays(monday, 6);
      const days: Date[] = [];
      for (let i = 0; i < 7; i++) days.push(addDays(monday, i));
      return { from: fmtDate(monday), to: fmtDate(sunday), days };
    }
    const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const start = startOfWeek(firstOfMonth);
    const days: Date[] = [];
    let d = start;
    while (days.length < 42) { days.push(new Date(d)); d = addDays(d, 1); }
    while (days.length > 35 && days[days.length - 7].getMonth() !== anchor.getMonth()) days.splice(-7);
    return { from: fmtDate(days[0]), to: fmtDate(days[days.length - 1]), days };
  }, [view, anchor]);

  const { data, isLoading } = useQuery({
    queryKey: ["therapist", "calendar", from, to],
    queryFn: () => apiGet<{ sessions: CalendarSession[] }>(`/api/therapist/calendar?from=${from}&to=${to}`, "Failed"),
  });

  const sessions = data?.sessions ?? [];

  const sessionsByDate = useMemo(() => {
    const map: Record<string, CalendarSession[]> = {};
    for (const s of sessions) {
      // Bucket by the Sarajevo calendar date, not the browser's — otherwise a
      // near-midnight session lands in the wrong day cell while its rendered
      // time (also Sarajevo) contradicts that cell.
      const key = utcToWallClock(s.scheduledAt).date;
      if (!map[key]) map[key] = [];
      map[key].push(s);
    }
    return map;
  }, [sessions]);

  const invalidateCalendar = () => {
    queryClient.invalidateQueries({ queryKey: ["therapist", "calendar"] });
    // A booking/cancel/reschedule changes slot availability too.
    queryClient.invalidateQueries({ queryKey: ["available-slots"] });
  };

  const rescheduleMutation = useMutation({
    mutationFn: ({ id, date, time }: { id: string; date: string; time: string }) =>
      apiSend(`/api/sessions/${id}/reschedule`, "POST", { date, time }, t("therapist.therapies.errorRescheduling")),
    onSuccess: () => {
      invalidateCalendar();
      setReschedulingSession(null);
      toast.success(t("therapist.therapies.rescheduleSuccess"));
    },
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      apiSend<{ pendingApproval?: boolean }>(`/api/sessions/${id}/cancel`, "POST", { reason }, "Greška pri otkazivanju"),
    onSuccess: (data) => {
      invalidateCalendar();
      setCancelingSession(null);
      toast.success(data?.pendingApproval ? "Zahtjev je poslan adminu na odobrenje" : "Termin otkazan");
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: (requestId: string) =>
      apiSend(`/api/therapist/cancellation-requests/${requestId}/withdraw`, "POST", {}, "Greška pri povlačenju zahtjeva"),
    onSuccess: () => {
      invalidateCalendar();
      toast.success("Zahtjev je povučen");
    },
  });

  const addSessionMutation = useMutation({
    mutationFn: (body: ManualBookingBody) =>
      apiSend("/api/therapist/sessions", "POST", body, "Greška pri kreiranju termina"),
    onSuccess: () => {
      invalidateCalendar();
      setShowAddSession(false);
      toast.success("Termin kreiran");
    },
  });

  const releaseHoldMutation = useMutation({
    mutationFn: (id: string) => apiSend(`/api/therapist/sessions/${id}/release`, "POST", {}, "Greška pri oslobađanju"),
    onSuccess: () => { invalidateCalendar(); toast.success("Termin oslobođen"); },
  });
  const extendHoldMutation = useMutation({
    mutationFn: ({ id, holdHours }: { id: string; holdHours: number }) =>
      apiSend(`/api/therapist/sessions/${id}/extend`, "POST", { holdHours }, "Greška pri produženju"),
    onSuccess: () => { invalidateCalendar(); toast.success("Rok produžen"); },
  });

  const goBack = () => {
    if (view === "week") setAnchor(addDays(anchor, -7));
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1));
  };
  const goForward = () => {
    if (view === "week") setAnchor(addDays(anchor, 7));
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1));
  };
  const goToday = () => { setAnchor(today); setSelectedDate(today); };

  const title = useMemo(() => {
    if (view === "week") {
      const mon = startOfWeek(anchor);
      const sun = addDays(mon, 6);
      if (mon.getMonth() === sun.getMonth())
        return `${mon.getDate()}. — ${sun.getDate()}. ${monthNames[mon.getMonth()]} ${mon.getFullYear()}`;
      return `${mon.getDate()}. ${monthNames[mon.getMonth()].substring(0, 3)} — ${sun.getDate()}. ${monthNames[sun.getMonth()].substring(0, 3)} ${sun.getFullYear()}`;
    }
    return `${monthNames[anchor.getMonth()]} ${anchor.getFullYear()}`;
  }, [view, anchor, monthNames]);

  // Navigacija: u mjesečnom prikazu chevroni nose naziv susjednog mjeseca;
  // u sedmičnom strelice ostaju čiste (period nosi naslov stranice).
  const { prevLabel, nextLabel } = useMemo(() => {
    if (view === "month") {
      const prev = new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1);
      const next = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1);
      return {
        prevLabel: monthNames[prev.getMonth()],
        nextLabel: monthNames[next.getMonth()],
      };
    }
    return { prevLabel: null, nextLabel: null };
  }, [view, anchor, monthNames]);

  const selectedSessions = selectedDate ? (sessionsByDate[fmtDate(selectedDate)] || []) : [];

  return (
    <div className="relative mx-auto max-w-6xl space-y-5 pb-14 sm:space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl" style={{ fontFamily: "var(--font-heading)" }}>
            {title}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("calendar.subtitle")}</p>
        </div>

        <button
          onClick={() => setShowAddSession(true)}
          className="group inline-flex shrink-0 items-center gap-2 self-start rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm shadow-primary/20 transition-all hover:shadow-md hover:shadow-primary/30 hover:brightness-110 active:scale-[0.98] sm:self-auto"
        >
          <Plus className="size-4 transition-transform group-hover:rotate-90" weight="bold" />
          Dodaj termin
        </button>
      </div>

      <CalendarSummary
        sessions={sessions}
        selectedSessions={selectedSessions}
        selectedDate={selectedDate}
        isLoading={isLoading}
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex min-w-0 items-center gap-3">
          <CalendarDots className="size-5 shrink-0 text-primary" />
          <h2 className="text-lg font-semibold text-foreground" style={{ fontFamily: "var(--font-heading)" }}>{t("calendar.scheduleView")}</h2>
          {isLoading && <div className="size-4 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />}
        </div>

        <div className="flex flex-wrap items-center gap-2.5 sm:ml-auto">
          {/* Prikaz: sedmica / mjesec + Danas */}
          <div className="flex h-10 items-center gap-1 rounded-xl border border-border/70 bg-card/60 p-1 shadow-sm">
            {(["week", "month"] as const).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                aria-pressed={view === v}
                className={cn(
                  "h-full rounded-lg px-4 text-sm font-medium transition-all duration-200",
                  view === v
                    ? "bg-primary text-primary-foreground shadow-sm shadow-primary/25"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {t(`calendar.${v}`)}
              </button>
            ))}
            <div className="mx-0.5 h-5 w-px bg-border/70" />
            <button
              onClick={goToday}
              className="h-full rounded-lg px-4 text-sm font-medium text-muted-foreground transition-all duration-200 hover:bg-muted/60 hover:text-foreground"
            >
              {t("calendar.today")}
            </button>
          </div>

          {/* Navigacija: ‹ prethodni mjesec · sljedeći mjesec › */}
          <div className="flex h-10 items-stretch overflow-hidden rounded-xl border border-border/70 bg-card/60 shadow-sm">
            <button
              onClick={goBack}
              aria-label={t("calendar.previous")}
              className="group flex h-full items-center gap-1.5 px-3.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
            >
              <CaretLeft className="size-4 shrink-0 transition-transform group-hover:-translate-x-0.5" weight="bold" />
              {prevLabel && <span>{prevLabel}</span>}
            </button>
            <div className="w-px self-stretch bg-border/70" />
            <button
              onClick={goForward}
              aria-label={t("calendar.next")}
              className="group flex h-full items-center gap-1.5 px-3.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
            >
              {nextLabel && <span>{nextLabel}</span>}
              <CaretRight className="size-4 shrink-0 transition-transform group-hover:translate-x-0.5" weight="bold" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-5 xl:flex-row xl:items-start">
        <div className="min-w-0 flex-1">
          <CalendarGrid
            days={days}
            view={view}
            anchor={anchor}
            today={today}
            selectedDate={selectedDate}
            sessionsByDate={sessionsByDate}
            onSelectDate={setSelectedDate}
            availabilityRules={scheduleOverview ?? DEFAULT_SCHEDULE_OVERVIEW}
          />
          <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl border border-border/50 bg-card px-3 py-2 text-[11px] text-muted-foreground shadow-sm">
            {Object.entries(STATUS_STYLES).map(([key, style]) => (
              <div key={key} className="flex items-center gap-1.5">
                <span className={cn("size-2 rounded-full", style.dot)} />
                <span>{t(STATUS_KEYS[key])}</span>
              </div>
            ))}
          </div>
        </div>

        <DaySidebar
          selectedDate={selectedDate}
          sessions={selectedSessions}
          dayNames={dayNames}
          monthNames={monthNames}
          onReschedule={setReschedulingSession}
          onCancel={setCancelingSession}
          onEditMembers={setMembersSession}
          onWithdrawCancelRequest={(s) => s.cancelRequestId && withdrawMutation.mutate(s.cancelRequestId)}
          onReleaseHold={(id) => releaseHoldMutation.mutate(id)}
          onExtendHold={(id, holdHours) => extendHoldMutation.mutate({ id, holdHours })}
        />
      </div>

      {reschedulingSession && meData?.id && (
        <RescheduleDialog
          session={{ id: reschedulingSession.id, scheduledAt: reschedulingSession.scheduledAt, therapistId: meData.id, durationMinutes: reschedulingSession.durationMinutes }}
          onClose={() => setReschedulingSession(null)}
          onConfirm={(data) => rescheduleMutation.mutate(data)}
          isPending={rescheduleMutation.isPending}
        />
      )}

      {membersSession && (
        <SessionMembersDialog
          session={membersSession}
          onClose={() => setMembersSession(null)}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ["therapist", "calendar"] })}
        />
      )}

      {cancelingSession && (
        <CancelSessionDialog
          session={cancelingSession}
          onClose={() => setCancelingSession(null)}
          onConfirm={({ reason }) => cancelMutation.mutate({ id: cancelingSession.id, reason })}
          isPending={cancelMutation.isPending}
          requiresApproval={cancelingSession.paymentStatus === "paid"}
        />
      )}

      {showAddSession && meData?.id && (
        <AddSessionDialog
          therapistId={meData.id}
          onClose={() => setShowAddSession(false)}
          onConfirm={(body) => addSessionMutation.mutate(body)}
          isPending={addSessionMutation.isPending}
        />
      )}
    </div>
  );
}
